import {
  AiActionCapabilityV1Schema,
  applySmartCreationLocalReviewChanges,
  type CampaignCreationCapabilities,
  type CampaignCreationTask,
  type CampaignCreationTaskSubmissionInput,
  type SmartCreationBrowserDraft,
  SmartCreationBrowserDraftSchema,
  type SmartCreationDraft,
  type SmartCreationNegativeCandidates,
  type SmartCreationNegativeCandidatesInput,
  SmartCreationNegativeCandidatesResponseSchema,
  type SmartCreationObjectiveRecommendation,
  SmartCreationObjectiveRecommendationSchema,
  SmartCreationRecommendationEvidenceSchema,
  type SmartCreationReviewBatchChangeInput,
  SmartCreationReviewBatchChangeInputSchema,
  SmartCreationReviewBidBoundsSuggestionSchema,
  type SmartCreationReviewBidPreview,
  type SmartCreationReviewBidPreviewInput,
  SmartCreationReviewBidPreviewResponseSchema,
  SmartCreationReviewError,
  type SmartCreationReviewRefreshPreviewInput,
  type SmartCreationReviewSources,
  type SmartCreationReviewSourcesQuery,
  SmartCreationReviewSourcesResponseSchema,
  type SmartCreationStrategyOperation,
  type SmartCreationStructure,
  type SmartCreationStructureOptionsEvaluation,
  SmartCreationStructureOptionsEvaluationSchema,
  type SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { HTTPError } from "ky";
import { toast } from "sonner";
import { z } from "zod";
import { i18n } from "@/i18n";
import { api } from "@/lib/api";
import {
  fetchCampaignCreationTask,
  submitCampaignCreationTask,
} from "@/lib/campaign-creation-tasks";
import {
  clearLocalSmartCreationPlan,
  clearRejectedSmartCreationSubmission,
  prepareSmartCreationSubmission,
  readLocalSmartCreationPlan,
  readSmartCreationAcceptedTask,
  rememberSmartCreationAcceptedTask,
  smartCreationOwnerKey,
  writeLocalSmartCreationEditedPlan,
  writeLocalSmartCreationPlan,
} from "@/lib/smart-creation-draft-store";
import { clearSmartCreationLocalState } from "@/lib/smart-creation-local-state";

// Product validation can include multiple Amazon eligibility batches under a 60-second server deadline.
const PRODUCT_VALIDATION_TIMEOUT_MS = 120_000;
const shopHeaders = (shopId: string) => ({ "X-Shop-Id": shopId });

function missingBrowserDraft(): Error {
  return new Error(i18n.t("smartCreation:errors.browserDraftMissing"));
}

async function parseDraft(response: Promise<unknown>): Promise<SmartCreationBrowserDraft> {
  const owner = smartCreationOwnerKey();
  const parsed = z
    .object({
      success: z.literal(true),
      data: SmartCreationBrowserDraftSchema,
      draftSignature: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .strict()
    .parse(await response);
  if (
    owner === smartCreationOwnerKey() &&
    !writeLocalSmartCreationPlan(parsed.data, parsed.draftSignature)
  ) {
    toast.warning(i18n.t("smartCreation:errors.localStorage"), { id: "smart-creation-storage" });
  }
  return parsed.data;
}

async function evaluateLocalDraft(planId: string, shopId: string, action: string, input: unknown) {
  const local = readLocalSmartCreationPlan(planId, shopId);
  if (!local) throw missingBrowserDraft();
  if (local.submission) throw new Error(i18n.t("smartCreation:task.submissionPending"));
  return api
    .post("smart-creation/drafts/evaluate", {
      headers: shopHeaders(shopId),
      json: { plan: local.signedPlan ?? local.plan, signature: local.signature, action, input },
      retry: 0,
      timeout: PRODUCT_VALIDATION_TIMEOUT_MS,
    })
    .json<unknown>();
}

const ObjectiveResponseSchema = z
  .object({ success: z.literal(true), data: SmartCreationObjectiveRecommendationSchema })
  .strict();
const BrandsResponseSchema = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        brands: z.array(z.string().trim().min(1)),
        missingSkus: z.array(z.string().trim().min(1)),
      })
      .strict(),
  })
  .strict();
const ActionCapabilitiesResponseSchema = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        shopId: z.string().uuid(),
        planId: z.string().uuid(),
        capabilities: z.array(AiActionCapabilityV1Schema),
        bidBoundsSuggestion: SmartCreationReviewBidBoundsSuggestionSchema.optional(),
      })
      .strict(),
  })
  .strict();
const StructuresResponseSchema = z
  .object({ success: z.literal(true), data: SmartCreationStructureOptionsEvaluationSchema })
  .strict();

export async function createSmartCreationPlan(shopId: string): Promise<SmartCreationBrowserDraft> {
  const local = readLocalSmartCreationPlan(undefined, shopId);
  if (local) return fetchSmartCreationPlan(local.plan.id, shopId);
  return parseDraft(
    api
      .post("smart-creation/drafts", { headers: shopHeaders(shopId), json: {}, retry: 0 })
      .json<unknown>(),
  );
}

export async function fetchSmartCreationPlan(
  planId: string,
  shopId?: string,
): Promise<SmartCreationBrowserDraft> {
  const local = readLocalSmartCreationPlan(planId, shopId);
  if (!local) throw missingBrowserDraft();
  return local.plan;
}

export async function cancelSmartCreationPlan(
  planId: string,
  shopId: string,
  _expectedRevision: number,
): Promise<void> {
  if (!clearLocalSmartCreationPlan(planId, shopId))
    toast.warning(i18n.t("smartCreation:errors.localStorageCleanup"));
  clearSmartCreationLocalState(shopId, planId);
}

export async function previewSmartCreationProductHistory(
  planId: string,
  shopId: string,
  draft: SmartCreationDraft,
) {
  return z
    .object({ success: z.literal(true), data: SmartCreationRecommendationEvidenceSchema })
    .strict()
    .parse(await evaluateLocalDraft(planId, shopId, "history", { draft })).data;
}

export async function fetchSmartCreationActionCapabilities(planId: string, shopId: string) {
  return ActionCapabilitiesResponseSchema.parse(
    await evaluateLocalDraft(planId, shopId, "capabilities", {}),
  ).data;
}

export async function fetchSmartCreationSelectedBrands(planId: string, shopId: string) {
  return BrandsResponseSchema.parse(await evaluateLocalDraft(planId, shopId, "brands", {})).data;
}

export async function fetchSmartCreationReviewSources(
  planId: string,
  shopId: string,
  query: Partial<SmartCreationReviewSourcesQuery> = {},
): Promise<SmartCreationReviewSources> {
  return SmartCreationReviewSourcesResponseSchema.parse(
    await evaluateLocalDraft(planId, shopId, "sources", query),
  ).data;
}

export async function fetchSmartCreationReviewBidPreview(
  planId: string,
  shopId: string,
  input: SmartCreationReviewBidPreviewInput,
): Promise<SmartCreationReviewBidPreview> {
  return SmartCreationReviewBidPreviewResponseSchema.parse(
    await evaluateLocalDraft(planId, shopId, "bids", {
      ...input,
      editedReview: readLocalSmartCreationPlan(planId, shopId)?.plan.reviewWorkspace,
    }),
  ).data;
}

export async function fetchSmartCreationNegativeCandidates(
  shopId: string,
  input: SmartCreationNegativeCandidatesInput,
): Promise<SmartCreationNegativeCandidates> {
  const response = await api
    .post("smart-creation/drafts/negative-candidates", {
      headers: shopHeaders(shopId),
      json: input,
      retry: 0,
    })
    .json<unknown>();
  return SmartCreationNegativeCandidatesResponseSchema.parse(response).data;
}

export function updateSmartCreationPlan(
  planId: string,
  shopId: string,
  expectedRevision: number,
  draft: SmartCreationDraft,
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "update", { expectedRevision, draft }));
}

export function generateSmartCreationPreviewPlan(
  planId: string,
  shopId: string,
  expectedRevision: number,
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "compile", { expectedRevision }));
}

export async function previewSmartCreationObjectiveRecommendation(
  planId: string,
  shopId: string,
  draft: SmartCreationDraft,
): Promise<SmartCreationObjectiveRecommendation> {
  return ObjectiveResponseSchema.parse(
    await evaluateLocalDraft(planId, shopId, "objective", { draft }),
  ).data;
}

export function generateSmartCreationStrategyRecommendation(
  planId: string,
  shopId: string,
  expectedRevision: number,
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "recommend", { expectedRevision }));
}

export function acceptSmartCreationStrategyRecommendation(
  planId: string,
  shopId: string,
  input: {
    expectedRevision: number;
    recommendationId: string;
    strategies: SmartCreationTargetingStrategyKind[];
  },
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "accept", input));
}

export function updateSmartCreationStrategy(
  planId: string,
  shopId: string,
  input: { expectedRevision: number; operations: SmartCreationStrategyOperation[] },
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "strategy", input));
}

export function updateSmartCreationPlanStructure(
  planId: string,
  shopId: string,
  input: { expectedRevision: number; structure: SmartCreationStructure },
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "structure", input));
}

export async function evaluateSmartCreationPlanStructures(
  planId: string,
  shopId: string,
  expectedRevision: number,
): Promise<SmartCreationStructureOptionsEvaluation> {
  return StructuresResponseSchema.parse(
    await evaluateLocalDraft(planId, shopId, "structures", { expectedRevision }),
  ).data;
}

function acceptTask(planId: string, shopId: string, task: CampaignCreationTask) {
  if (rememberSmartCreationAcceptedTask(planId, shopId, task.id)) {
    if (!clearLocalSmartCreationPlan(planId, shopId))
      toast.warning(i18n.t("smartCreation:errors.localStorageCleanup"));
    clearSmartCreationLocalState(shopId, planId);
  } else {
    toast.warning(i18n.t("smartCreation:errors.localStorageCleanup"));
  }
  return task;
}

export async function submitSmartCreationDraft(
  planId: string,
  shopId: string,
  input: Pick<
    CampaignCreationTaskSubmissionInput["input"],
    "expectedPlanRevision" | "expectedWorkspaceRevision" | "contentHash"
  >,
): Promise<CampaignCreationTask> {
  const owner = smartCreationOwnerKey();
  // The pending snapshot stays under its original owner if the user changes workspace mid-request.
  const accept = (task: CampaignCreationTask) =>
    owner === smartCreationOwnerKey() ? acceptTask(planId, shopId, task) : task;
  const receipt = readSmartCreationAcceptedTask(planId);
  if (receipt) return fetchCampaignCreationTask(shopId, receipt.taskId);
  const local = readLocalSmartCreationPlan(planId, shopId);
  if (!local) throw missingBrowserDraft();
  if (local.submission) {
    try {
      return accept(await fetchCampaignCreationTask(shopId, local.submission.submissionId));
    } catch (error) {
      // Only an authoritative 404 allows replay of the exact immutable submission.
      if (!(error instanceof HTTPError) || error.response.status !== 404) throw error;
    }
  }
  if (owner !== smartCreationOwnerKey()) throw missingBrowserDraft();
  const signed = SmartCreationBrowserDraftSchema.parse(local.signedPlan ?? local.plan);
  const baseline = signed.reviewWorkspace;
  const currentReview = local.plan.reviewWorkspace;
  if (!baseline || !currentReview) throw new Error("Compile the draft before creating");
  if (
    !local.submission &&
    (input.expectedPlanRevision !== local.plan.revision ||
      input.expectedWorkspaceRevision !== currentReview.workspaceRevision ||
      input.contentHash !== currentReview.contentHash)
  ) {
    throw new SmartCreationReviewError(
      409,
      "smart_creation.review_revision_conflict",
      "The local draft changed in another tab; review the latest contents before creating",
    );
  }
  const request = prepareSmartCreationSubmission(planId, shopId, {
    submissionId: crypto.randomUUID(),
    plan: signed,
    signature: local.signature,
    input: {
      expectedPlanRevision: signed.revision,
      expectedWorkspaceRevision: baseline.workspaceRevision,
      contentHash: baseline.contentHash,
      editedReview: currentReview,
    },
  });
  try {
    return accept(await submitCampaignCreationTask(shopId, request));
  } catch (error) {
    if (owner === smartCreationOwnerKey() && isDeterministicRejection(error)) {
      try {
        return accept(await fetchCampaignCreationTask(shopId, request.submissionId));
      } catch (readError) {
        if (owner === smartCreationOwnerKey() && isNotFound(readError)) {
          clearRejectedSmartCreationSubmission(planId, shopId, request.submissionId);
        }
      }
    }
    throw error;
  }
}

// 401/408/429 are retryable transport outcomes; every other 4xx means the API refused this snapshot.
const RETRYABLE_CLIENT_STATUSES = new Set([401, 408, 429]);

function isDeterministicRejection(error: unknown): boolean {
  if (!(error instanceof HTTPError)) return false;
  const status = error.response.status;
  return status >= 400 && status < 500 && !RETRYABLE_CLIENT_STATUSES.has(status);
}

function isNotFound(error: unknown): boolean {
  return error instanceof HTTPError && error.response.status === 404;
}

/**
 * Unlocks a pending submission only after the API proves it was never accepted.
 * Returns the accepted task instead when the lookup finds one.
 */
export async function discardSmartCreationPendingSubmission(
  planId: string,
  shopId: string,
): Promise<CampaignCreationTask | null> {
  const owner = smartCreationOwnerKey();
  const submission = readLocalSmartCreationPlan(planId, shopId)?.submission;
  if (!submission) return null;
  try {
    const task = await fetchCampaignCreationTask(shopId, submission.submissionId);
    return owner === smartCreationOwnerKey() ? acceptTask(planId, shopId, task) : task;
  } catch (error) {
    if (!isNotFound(error)) throw error;
  }
  if (
    owner === smartCreationOwnerKey() &&
    !clearRejectedSmartCreationSubmission(planId, shopId, submission.submissionId)
  )
    toast.warning(i18n.t("smartCreation:errors.localStorage"), { id: "smart-creation-storage" });
  return null;
}

export function updateSmartCreationReviewWorkspace(
  planId: string,
  shopId: string,
  input: SmartCreationReviewBatchChangeInput,
  capabilities?: CampaignCreationCapabilities,
): Promise<SmartCreationBrowserDraft> {
  const local = readLocalSmartCreationPlan(planId, shopId);
  if (!local) return Promise.reject(missingBrowserDraft());
  const parsed = SmartCreationReviewBatchChangeInputSchema.parse(input);
  const current = local.plan;
  const workspace = current.reviewWorkspace;
  if (!workspace || current.status !== "preview_ready" || !capabilities)
    return Promise.reject(
      new Error("The current preview or its marketplace limits are unavailable"),
    );
  if (
    parsed.expectedPlanRevision !== current.revision ||
    parsed.expectedWorkspaceRevision !== workspace.workspaceRevision
  ) {
    return Promise.reject(
      new SmartCreationReviewError(
        409,
        "smart_creation.review_revision_conflict",
        "The local draft changed in another tab; reload before editing",
      ),
    );
  }
  try {
    const reviewWorkspace = applySmartCreationLocalReviewChanges(
      workspace,
      parsed.changes,
      capabilities,
      new Date(),
      current.draft.promotionDateRange,
    );
    const next = {
      ...current,
      reviewWorkspace,
      previewDraft: reviewWorkspace.draftContent,
      updatedAt: new Date().toISOString(),
    };
    if (!writeLocalSmartCreationEditedPlan(next))
      toast.warning(i18n.t("smartCreation:errors.localStorage"), { id: "smart-creation-storage" });
    return Promise.resolve(next);
  } catch (error) {
    return Promise.reject(error);
  }
}

export function refreshPreviewSmartCreationReviewWorkspace(
  planId: string,
  shopId: string,
  input: SmartCreationReviewRefreshPreviewInput,
): Promise<SmartCreationBrowserDraft> {
  return parseDraft(evaluateLocalDraft(planId, shopId, "recompile", input));
}
